import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { ArrowLeft, Shield, FileText } from 'lucide-react';
import Seo from '../components/Seo';

const Policies = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API_URL}/policies`);
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title="Platform Policies"
        description="Platform policies for I Need A Social Worker covering job data sources, memberships, refunds, and acceptable use across our social work jobs and resource platform."
        path="/policies"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="font-serif text-3xl font-bold text-gray-900 mb-8">
          Policies & Terms
        </h1>

        {/* Privacy Policy */}
        <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <h2 className="font-serif text-xl font-semibold text-gray-900">
              Privacy Policy
            </h2>
          </div>
          <div className="prose prose-sm max-w-none text-gray-600">
            <p className="whitespace-pre-line">
              {policies?.privacy_policy_summary || `Privacy Summary:
- We collect only necessary information for job matching.
- Payment data is processed securely by Stripe/PayPal.
- We never sell your personal information.
- You can request data deletion at any time.`}
            </p>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3">Data We Collect</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• <strong>Job Seekers:</strong> Name, email, phone, resume, license information</li>
              <li>• <strong>Employers:</strong> Company name, contact info, job postings</li>
              <li>• <strong>Payment Data:</strong> Processed securely by Stripe/PayPal (we never store card numbers)</li>
            </ul>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3">Your Rights</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Access your personal data</li>
              <li>• Request data correction or deletion</li>
              <li>• Opt out of marketing communications</li>
              <li>• Export your data</li>
            </ul>
          </div>
        </div>

        {/* Refund Policy */}
        <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-secondary" />
            </div>
            <h2 className="font-serif text-xl font-semibold text-gray-900">
              Refund Policy
            </h2>
          </div>
          <div className="prose prose-sm max-w-none text-gray-600">
            <p className="whitespace-pre-line">
              {policies?.refund_policy || `Refund Policy:
- Monthly subscriptions: No refunds after 7 days of purchase.
- 6-Month & Annual plans: Pro-rated refund within first 30 days.
- Contact support@dsws.com for refund requests.`}
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3">Refund Details by Plan</h3>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">Monthly Plan ($149/mo)</p>
                <p className="text-sm text-gray-600">Full refund within 7 days. No refund after 7 days.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">6-Month Plan ($699)</p>
                <p className="text-sm text-gray-600">Pro-rated refund within 30 days based on unused months.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium text-gray-900">Annual Plan ($1,199)</p>
                <p className="text-sm text-gray-600">Pro-rated refund within 30 days based on unused months.</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3">How to Request a Refund</h3>
            <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
              <li>Email support@dsws.com with your account email</li>
              <li>Include your subscription plan and purchase date</li>
              <li>Explain the reason for your refund request</li>
              <li>Refunds are processed within 5-10 business days</li>
            </ol>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-primary/5 rounded-xl p-6 text-center">
          <h3 className="font-semibold text-gray-900 mb-2">Questions?</h3>
          <p className="text-gray-600 mb-4">
            Contact us at <a href="mailto:support@dsws.com" className="text-primary hover:underline">support@dsws.com</a>
          </p>
          <p className="text-sm text-gray-500">
            Diversified Social Work Solutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default Policies;
